/**
 * Activity — what happened here, and who did it.
 *
 * This nav item has been marked `ready: false` since the nav existed, because
 * both of its sources were finished schema with nothing writing to them:
 * `events` since migration 004, `audit_log` since 001. Both have writers now.
 *
 * ── One list, two kinds of row, and the difference kept visible ──────────
 *
 * A SYSTEM row is something the platform observed — a device came back. A
 * PERSON row is something somebody did — a site created, an alert
 * acknowledged. They live in separate tables on purpose, and the reason is a
 * defect: the predecessor modelled recoveries as alerts with severity 'info'
 * and produced the VIDEO_RESTORED-without-VIDEO_LOSS inversion, announcing a
 * recovery for a fault nobody had been told about.
 *
 * Merging them for display is a convenience; blurring them would not be. So
 * each row says which it is, and the two are visually distinguishable without
 * relying on colour alone — the actor column is empty for a system row, and
 * that emptiness is the signal rather than a missing value.
 */

import { useEffect, useMemo, useState } from 'react';

import { api, ApiError, type ActivityResponse, type ActivityRow } from './api.ts';
import {
  Banner,
  DataTable,
  Empty,
  Loading,
  Pill,
  Section,
  type ColumnDef,
} from './components/index.ts';

/** Actions the platform performs, mapped to how they should read. */
const SOURCE_LABEL: Record<string, string> = {
  event: 'System',
  audit: 'Person',
};

const SOURCE_REASON: Record<string, string> = {
  event: 'Something the platform observed — a device came back, a check changed.',
  audit: 'Something a person did in this console.',
};

/**
 * `site.create` → `created`, `alert.acknowledge` → `acknowledged`.
 *
 * The action keys are machine names and belong in the row for anyone reading
 * carefully; the summary already carries the sentence. This is the middle
 * ground — legible without pretending the key is prose.
 */
function actionLabel(row: ActivityRow): string {
  if (row.source === 'event') return row.action;
  const verb = row.action.split('.').slice(1).join('.');
  return verb === '' ? row.action : verb;
}

function whatItWasAbout(row: ActivityRow): string {
  if (row.asset_code !== null && row.asset_code !== '') return row.asset_code;
  if (row.asset_name !== null && row.asset_name !== '') return row.asset_name;
  return '—';
}

/** A row with a key: two rows can share a timestamp, and neither table's id is exposed here. */
type KeyedRow = ActivityRow & { key: string };

const COLUMNS: ColumnDef<KeyedRow, unknown>[] = [
  {
    id: 'when',
    header: 'When',
    accessorKey: 'at',
    cell: ({ getValue }) => (
      <span className="small muted">{new Date(getValue() as string).toLocaleString()}</span>
    ),
  },
  {
    id: 'source',
    header: 'Source',
    accessorKey: 'source',
    cell: ({ getValue }) => {
      const source = getValue() as string;
      return <Pill reason={SOURCE_REASON[source] ?? null}>{SOURCE_LABEL[source] ?? source}</Pill>;
    },
    size: 96,
  },
  {
    id: 'what',
    header: 'What',
    accessorFn: (r) => actionLabel(r),
    cell: ({ getValue }) => <span className="mono small">{getValue() as string}</span>,
  },
  {
    id: 'about',
    header: 'About',
    accessorFn: (r) => whatItWasAbout(r),
    cell: ({ getValue }) => <span className="mono small muted">{getValue() as string}</span>,
  },
  {
    id: 'who',
    header: 'Who',
    // Empty, deliberately, for a system row. A dash would read as "we do not
    // know who", which is different from "nobody".
    accessorFn: (r) => (r.source === 'audit' ? (r.actor_name ?? 'unknown user') : ''),
    cell: ({ getValue }) => <span className="small">{getValue() as string}</span>,
  },
  {
    id: 'detail',
    header: 'Detail',
    accessorKey: 'summary',
    enableSorting: false,
    cell: ({ getValue }) => <span className="small muted">{getValue() as string}</span>,
  },
];

export function ActivityView(): React.JSX.Element {
  const [data, setData] = useState<ActivityResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .get<ActivityResponse>('/api/activity?limit=100')
      .then((body) => live && setData(body))
      .catch((err: unknown) =>
        live && setError(err instanceof ApiError ? err.message : 'could not load activity'),
      );
    return (): void => {
      live = false;
    };
  }, []);

  const rows = useMemo<KeyedRow[]>(
    () => (data?.activity ?? []).map((row, index) => ({ ...row, key: `${row.source}-${row.at}-${index}` })),
    [data],
  );

  if (error !== null) return <Banner tone="fault">{error}</Banner>;
  if (data === null) return <Loading rows={8} cols={6} label="Loading activity" />;

  if (data.activity.length === 0) {
    /*
     * The same ambiguity the alerts screen has to resolve: "nothing has
     * happened" and "nothing is being recorded" look identical, and this screen
     * spent its whole life so far being the second one.
     */
    return (
      <Empty
        title="Nothing recorded yet"
        body="Recoveries the collector observed and changes people made. An empty list means nothing has happened since these records began, not that nothing is being kept."
      />
    );
  }

  return (
    <div className="stack">
      {data.window.truncated && (
        <Banner tone="warn">
          Showing the most recent {data.window.limit}. There is more history than this — a full
          page is a page boundary, not the beginning of the record.
        </Banner>
      )}

      <Section flush title="Timeline" summary="What the platform observed and what people did, newest first.">
        <DataTable
          data={rows}
          columns={COLUMNS}
          getRowId={(r) => r.key}
          initialSort={[{ id: 'when', desc: true }]}
          caption="Activity"
        />
      </Section>
    </div>
  );
}
